import { PAYMENT_TYPE_PARAMS } from 'constants/global'
import { PAYMENT_TYPE } from 'constants/global'
import { PARAM_IS_EMBEDDED_VIEW } from 'constants/params'
import { PARAM_PAYMENT_METHOD } from 'constants/params'
import { PARAM_APIKEY } from 'constants/params'
import { PARAM_REFER_USER_ID } from 'constants/params'
import { PARAM_REFER_STATION_ID } from 'constants/params'
import { PARAM_IS_HEADER_MINI_APP } from 'constants/params'
import { PARAM_IS_HEADER_MINI_APP_EXPAND } from 'constants/params'
import { PARAM_IS_TBPN_SELECT_VEHICLE_TYPE, PARAM_IS_TBPN_SELECT_VEHICLE_TYPE_WITH_ELECTRIC_MOTOBIKE, PARAM_IS_SELECT_VEHICLE_TYPE_USE_ICON } from 'constants/params'
import { PARAM_HIDE_PAYMENT_METHOD } from 'constants/params'
import { PARAM_PHONE_NUMBER } from 'constants/params'
import { PARAM_PARTNER_NAME } from 'constants/params'
import { LocalStorageManager } from 'helper/localStorage'
import { SessionStorageManager } from 'helper/localStorage'
import { smartParseParam } from 'helper/params'
import { getUrlParamValue } from 'helper/params'
import React from 'react'
import PropTypes from 'prop-types'
import { PARAM_SDK_PHONE_NUMBER } from 'constants/params'
import { PARAM_SDK_FULL_NAME } from 'constants/params'
import { PARAM_SDK_USERNAME, PARAM_SDK_TOKEN } from 'constants/params'
import { PARAM_APP_THEME_NAME } from 'constants/params'
import { PARAM_SHOW_PHONE_INPUT } from 'constants/params'
import { PARAM_BACK_TO_HOME_MINI_APP_URL } from 'constants/params'
import { PARAM_IS_DISPLAY_REFER_USER_CODE, PARAM_REFER_USER_CODE, PARAM_IS_DISPLAY_PROMOTION_CODE } from 'constants/params'

export const AppParamsContext = React.createContext(null)

const resolveParamsMiniAppSessionStorage = (config) => {
  // 1. param (raw)
  const param = getUrlParamValue(config.paramKey)

  // Xác định Storage cần dùng:
  // - Nếu là WebView MiniApp (REACT_APP_IS_WEBVIEW_MINIAPP = 1 hoặc true), lưu thẳng vào LocalStorage 
  // - Nếu không phải, mặc định lưu vào SessionStorage
  const isWebViewMiniApp = process.env.REACT_APP_IS_WEBVIEW_MINIAPP === '1' || String(process.env.REACT_APP_IS_WEBVIEW_MINIAPP).toLowerCase() === 'true'
  const StorageManager = isWebViewMiniApp ? LocalStorageManager : SessionStorageManager

  // có param thì lưu (raw)
  if (param !== null && param !== undefined && config.storageKey) {
    StorageManager.setItem(config.storageKey, param)
  }

  // 2. resolve raw theo thứ tự ưu tiên
  const storageValue = StorageManager.getItem(config.storageKey)
  const envValue = smartParseParam(process.env?.[config.envKey])
  const raw = param ?? storageValue ?? envValue ?? config.defaultValue

  // 3. parse giá trị cuối cùng
  return config.parser ? config.parser(raw) : raw
}

const resolveParamsMiniAppLocalStorage = (config) => {
  // 1. param (raw)
  const param = getUrlParamValue(config.paramKey)

  // có param thì lưu local (raw)
  if (param !== null && param !== undefined && config.storageKey) {
    LocalStorageManager.setItem(config.storageKey, param)
  }

  // 2. resolve raw theo thứ tự ưu tiên
  const storageValue = LocalStorageManager.getItem(config.storageKey)
  const envValue = smartParseParam(process.env?.[config.envKey])
  const raw = param ?? storageValue ?? envValue ?? config.defaultValue

  // 3. parse giá trị cuối cùng
  return config.parser ? config.parser(raw) : raw
}

/* =====================
 * PARAM MINI APP SCHEMA
 * ===================== */
const PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE = {
  apikey: {
    paramKey: PARAM_APIKEY,
    storageKey: PARAM_APIKEY,
    envKey: 'REACT_APP_APIKEY',
    defaultValue: undefined,
    parser: (v) => v
  },
  isEmbeddedView: {
    paramKey: PARAM_IS_EMBEDDED_VIEW,
    storageKey: PARAM_IS_EMBEDDED_VIEW,
    envKey: 'REACT_APP_PARAM_IS_EMBEDDED_VIEW',
    defaultValue: false,
    parser: (v) => v === true || v === 1
  },
  isHeaderMiniApp: {
    paramKey: PARAM_IS_HEADER_MINI_APP,
    storageKey: PARAM_IS_HEADER_MINI_APP,
    envKey: 'REACT_APP_PARAM_IS_HEADER_MINI_APP',
    defaultValue: undefined,
    parser: (v) => v
  },
  isHeaderMiniAppExpand: {
    paramKey: PARAM_IS_HEADER_MINI_APP_EXPAND,
    storageKey: PARAM_IS_HEADER_MINI_APP_EXPAND,
    envKey: '',
    defaultValue: false,
    parser: (v) => v === true || v === 1
  },
  backToHomeMiniAppUrlParam: {
    paramKey: PARAM_BACK_TO_HOME_MINI_APP_URL,
    storageKey: PARAM_BACK_TO_HOME_MINI_APP_URL,
    envKey: '',
    defaultValue: '',
    parser: (v) => v
  },
  paymentMethodType: {
    paramKey: PARAM_PAYMENT_METHOD,
    storageKey: PARAM_PAYMENT_METHOD,
    envKey: 'REACT_APP_PAYMENT_METHOD',
    defaultValue: undefined,
    parser: (v) => {
      const paymentMethod = (v || '').toLowerCase()
      const key = Object.keys(PAYMENT_TYPE_PARAMS).find((k) => PAYMENT_TYPE_PARAMS[k].some((item) => item.toLowerCase() === paymentMethod))
      return key ? PAYMENT_TYPE[key] : undefined
    }
  },
  isTBPNSelectVehicleType: {
    paramKey: PARAM_IS_TBPN_SELECT_VEHICLE_TYPE,
    storageKey: PARAM_IS_TBPN_SELECT_VEHICLE_TYPE,
    envKey: 'REACT_APP_PARAM_IS_TBPN_SELECT_VEHICLE_TYPE',
    defaultValue: false,
    parser: (v) => v === true || v === 1
  },
  isTBPNSelectVehicleTypeWithElectricMotobike: {
    paramKey: PARAM_IS_TBPN_SELECT_VEHICLE_TYPE_WITH_ELECTRIC_MOTOBIKE,
    storageKey: PARAM_IS_TBPN_SELECT_VEHICLE_TYPE_WITH_ELECTRIC_MOTOBIKE,
    defaultValue: false,
    parser: (v) => v === true || v === 1
  },
  isSelectVehicleTypeUseIcon: {
    paramKey: PARAM_IS_SELECT_VEHICLE_TYPE_USE_ICON,
    storageKey: PARAM_IS_SELECT_VEHICLE_TYPE_USE_ICON,
    envKey: '',
    defaultValue: false,
    parser: (v) => v === true || v === 1
  },
  hidePaymentMethod: {
    paramKey: PARAM_HIDE_PAYMENT_METHOD,
    storageKey: PARAM_HIDE_PAYMENT_METHOD,
    envKey: 'REACT_APP_HIDE_PAYMENT_METHOD',
    defaultValue: false,
    parser: (v) => v === true || v === 1
  },
  phoneNumber: {
    paramKey: PARAM_PHONE_NUMBER,
    storageKey: PARAM_PHONE_NUMBER,
    envKey: '',
    defaultValue: undefined,
    parser: (v) => v
  },
  partnerName: {
    paramKey: PARAM_PARTNER_NAME,
    storageKey: PARAM_PARTNER_NAME,
    envKey: '',
    defaultValue: undefined,
    parser: (v) => v
  },
  // Thông tin từ sdk đối tác
  sdkPhoneNumber: {
    paramKey: PARAM_SDK_PHONE_NUMBER,
    storageKey: PARAM_SDK_PHONE_NUMBER,
    envKey: '',
    defaultValue: undefined,
    parser: (v) => v
  },
  sdkFullName: {
    paramKey: PARAM_SDK_FULL_NAME,
    storageKey: PARAM_SDK_FULL_NAME,
    envKey: '',
    defaultValue: undefined,
    parser: (v) => v
  },
  sdkUsername: {
    paramKey: PARAM_SDK_USERNAME,
    storageKey: PARAM_SDK_USERNAME,
    envKey: '',
    defaultValue: undefined,
    parser: (v) => v
  },
  sdkToken: {
    paramKey: PARAM_SDK_TOKEN,
    storageKey: PARAM_SDK_TOKEN,
    envKey: '',
    defaultValue: undefined,
    parser: (v) => v
  },
  isDisplayReferUserCode: {
    paramKey: PARAM_IS_DISPLAY_REFER_USER_CODE,
    storageKey: PARAM_IS_DISPLAY_REFER_USER_CODE,
    envKey: '',
    defaultValue: true,
    parser: (v) => v !== false && v !== 0
  },
  referUserCode: {
    paramKey: PARAM_REFER_USER_CODE,
    storageKey: PARAM_REFER_USER_CODE,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => v
  },
  isDisplayPromotionCode: {
    paramKey: PARAM_IS_DISPLAY_PROMOTION_CODE,
    storageKey: PARAM_IS_DISPLAY_PROMOTION_CODE,
    envKey: '',
    defaultValue: true,
    parser: (v) => v !== false && v !== 0
  },
  showPhoneInput: {
    paramKey: PARAM_SHOW_PHONE_INPUT,
    storageKey: PARAM_SHOW_PHONE_INPUT,
    envKey: '',
    defaultValue: true,
    parser: (v) => v === true || v === 1
  },
  appThemeName: {
    paramKey: PARAM_APP_THEME_NAME,
    storageKey: PARAM_APP_THEME_NAME,
    envKey: 'REACT_APP_THEME_NAME',
    defaultValue: undefined,
    parser: (v) => v
  }
  // ------------------------
}

const PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE = {
  referUserId: {
    paramKey: PARAM_REFER_USER_ID,
    storageKey: PARAM_REFER_USER_ID,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => v
  },
  referStationId: {
    paramKey: PARAM_REFER_STATION_ID,
    storageKey: PARAM_REFER_STATION_ID,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => v
  }
}

export const AppParamsContextProvider = (props) => {
  const [updateCount, setUpdateCount] = React.useState(0)
  const updateParamsForAppParamsContext = () => setUpdateCount((c) => c + 1)

  const paramsMiniAppSessionStorage = React.useMemo(() => {
    const params = {}

    Object.keys(PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE).forEach((key) => {
      params[key] = resolveParamsMiniAppSessionStorage(PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE[key])
    })

    return params
  }, [updateCount])

  const paramsMiniAppLocalStorage = React.useMemo(() => {
    const params = {}

    Object.keys(PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE).forEach((key) => {
      params[key] = resolveParamsMiniAppLocalStorage(PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE[key])
    })

    return params
  }, [updateCount])

  // pass resolved params through context value
  return React.createElement(
    AppParamsContext.Provider,
    {
      value: {
        ...paramsMiniAppSessionStorage,
        ...paramsMiniAppLocalStorage,
        updateParamsForAppParamsContext
      }
    },
    props.children
  )
}
export const useAppParamsContext = () => {
  const context = React.useContext(AppParamsContext)
  if (!context) {
    throw new Error('useAppParamsContext must be used within AppParamsContextProvider')
  }
  return context
}

AppParamsContextProvider.propTypes = {
  children: PropTypes.node
}
